import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import Image from '@tiptap/extension-image';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { TableKit } from '@tiptap/extension-table';
import { Markdown } from 'tiptap-markdown';
import { MarkdownTable, TableCellGuard } from './markdownTableSupport.mjs';

// tiptap-markdown serializes images with prosemirror-markdown's INLINE image
// serializer, which never closes the block — but our Image node is block-level,
// so whatever block follows is emitted on the image's output line (a following
// "## Heading" comes back glued to the image and destroyed on reload). Same
// output as the default serializer, plus the missing closeBlock.
const BlockImage = Image.extend({
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        serialize(state, node) {
          const alt = state.esc(node.attrs.alt || '');
          const src = (node.attrs.src || '').replace(/[()]/g, '\\$&');
          const title = node.attrs.title ? ` "${node.attrs.title.replace(/"/g, '\\"')}"` : '';
          state.write(`![${alt}](${src}${title})`);
          if (node.isBlock) state.closeBlock(node);
        },
      },
    };
  },
});

// The one editor extension stack, shared by ActionEditor and the headless
// round-trip tests (client/test) — keyboard-shortcut precedence depends on
// extension order, so the tests must see exactly what the app ships.
export function buildEditorExtensions({ placeholder } = {}) {
  return [
    StarterKit.configure({
      link: { openOnClick: false, autolink: true, linkOnPaste: true },
      dropcursor: { color: 'var(--color-primary, #3b82f6)', width: 2 },
    }),
    Typography,
    BlockImage.configure({ allowBase64: true }),
    TaskList,
    TaskItem.configure({ nested: true }),
    // Notes take pasted GFM tables — without these nodes a pasted table
    // degrades to run-together text and is destroyed on save. The stock table
    // node is swapped for MarkdownTable, whose serializer can't fall back to
    // the "[table]" literal; TableCellGuard keeps cells single-block so they
    // stay cleanly serializable in the first place.
    TableKit.configure({ table: false }),
    MarkdownTable.configure({ resizable: false }),
    TableCellGuard,
    Markdown.configure({
      html: false,
      tightLists: true,
      transformPastedText: true,
      transformCopiedText: true,
    }),
    Placeholder.configure({
      placeholder:
        // The empty note says nothing — Bear's quiet (Paul, Oct 9 2026).
        placeholder || "",
    }),
  ];
}
